import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { tenantStorageUsageService } from "../services/tenantStorageUsageService";
import { tenantStorageUsageDisplayId } from "./TenantStorageUsageCommonStore";
import { TenantStorageUsage } from "../../../api/graphql";
import { StorageOperation } from "../../../api/graphql";
import { InitializeParams } from "../../../store/types";

export class TenantStorageUsageViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = tenantStorageUsageService;
  tenantStorageUsageInstance: TenantStorageUsage | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  operationValue: StorageOperation = "" as StorageOperation;
  bytesValue: number = 0;
  documentNameValue: string = "";
  documentIdValue: string = "";
  fileTypeValue: string = "";
  timestampValue: Date | null = null;
  balanceAfterValue: number = 0;

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadTenantStorageUsageById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.tenantStorageUsageOne({ id });
    this.tenantStorageUsageInstance = result.tenantStorageUsageOne;
    if (this.tenantStorageUsageInstance) {
      this.loadValues(this.tenantStorageUsageInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.tenantStorageUsageInstance) {
      return tenantStorageUsageDisplayId(
        this.tenantStorageUsageInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.TenantStorageUsageSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  returnedData(_params: CallbackDataParams): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  getOperationOptions() {
    return this.langStore.StorageOperationOptions;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.tenantStorageUsageInstance = null;
    this.operationValue = "" as StorageOperation;
    this.bytesValue = 0;
    this.documentNameValue = "";
    this.documentIdValue = "";
    this.fileTypeValue = "";
    this.timestampValue = null;
    this.balanceAfterValue = 0;
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelTenantStorageUsage() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editTenantStorageUsage() {
    const id = this.tenantStorageUsageInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/tenantStorageUsage/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printTenantStorageUsage(handlePrint: () => void): void {
    handlePrint();
  }

  *closeTenantStorageUsage(): any {
    yield this.close();
  }

  loadValues(instance: TenantStorageUsage) {
    this.idValue = instance.id;
    this.operationValue = instance.operation;
    this.bytesValue = instance.bytes;
    this.documentNameValue = instance.documentName;
    this.documentIdValue = instance.documentId;
    this.fileTypeValue = instance.fileType;
    this.timestampValue = new Date(instance.timestamp);
    this.balanceAfterValue = instance.balanceAfter;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadTenantStorageUsageById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
